// Satu sumber kebenaran untuk aritmetika total checkout, port dari
// mobile-user/src/screens/Checkout/paymentSummary.js supaya web dan mobile
// menghitung angka yang sama persis. Fungsinya murni sehingga daftar metode
// pembayaran bisa menghitung total per metode, bukan hanya untuk metode yang
// sedang dipilih.
const TRANSACTION_FEE_PRODUCT_TYPES = new Set([
  "ebilling",
  "mixed-ebilling",
  "emeterai",
  "mixed-emeterai",
]);

// QRIS dan GoPay sama-sama kena 1%, untuk e-billing maupun e-meterai. Aturan
// e-billing di server sempat menyebut QRIS saja karena ditulis Okt 2023 saat
// QRIS masih e-wallet yang aktif; sekarang disamakan dengan aturan e-meterai.
const PERCENTAGE_FEE_METHOD_IDS = new Set(["qris", "gopay"]);

// Transfer manual BRI dicocokkan ke pesanan lewat kode unik yang ditambahkan bank
// ke nominalnya, jadi total persisnya baru pasti setelah transfer dilakukan.
export const UNIQUE_CODE = { minimum: 1, maximum: 100 };

// Batas minimum ditentukan server per metode: checkout Lambda membaca
// `payment_method_detail['minimum']` dari payments.json, dan nilainya tidak
// seragam — gopay minimum 5.000 untuk e-meterai tetapi 10.000 di tempat lain,
// dan metode `voucher` minimumnya 0. Daftar metode yang dikirim ke klien sudah
// memuat `minimum` (Lambda hanya membuang `fee` dan `expiry`), jadi pakai angka
// itu. Konstanta di bawah hanya cadangan untuk pemanggil yang tidak punya objek
// metode dari server.
const FALLBACK_MINIMUM_QRIS = 5000;
const FALLBACK_MINIMUM = 10000;

const getMinimumPayment = (paymentMethod) => {
  const serverMinimum = paymentMethod?.minimum;
  // Sengaja bukan `||` dan bukan `Number()`: minimum 0 (metode voucher) itu
  // nilai sah, sedangkan `Number(null)` dan `Number("")` sama-sama 0 sehingga
  // metode tanpa minimum akan salah dibaca sebagai bebas batas.
  if (typeof serverMinimum === "number" && Number.isFinite(serverMinimum)) {
    return Math.max(serverMinimum, 0);
  }
  return paymentMethod?.id === "qris" ? FALLBACK_MINIMUM_QRIS : FALLBACK_MINIMUM;
};

const calculateApplicablePoint = (
  initialTotal,
  pointBalance,
  minimumPayment,
) => {
  let applicablePoint = 0;
  let remainingTotal = initialTotal;

  if (remainingTotal > 0 && pointBalance > 0) {
    applicablePoint = Math.min(remainingTotal, pointBalance);
  }

  remainingTotal -= applicablePoint;
  if (remainingTotal < 0) {
    applicablePoint += remainingTotal;
    remainingTotal = 0;
  }

  if (remainingTotal > 0 && remainingTotal < minimumPayment) {
    const difference = minimumPayment - remainingTotal;
    if (applicablePoint > difference) {
      applicablePoint -= difference;
    }
  }

  return applicablePoint;
};

export const calculatePaymentSummary = ({
  amount = 0,
  paymentMethod = {},
  pointBalance = 0,
  productType = "",
  usePoint = false,
}) => {
  const minimumPayment = getMinimumPayment(paymentMethod);
  let total = Math.max(Number(amount) || 0, 0);
  const availablePoint = Math.max(Number(pointBalance) || 0, 0);
  const pointUsed = usePoint
    ? calculateApplicablePoint(total, availablePoint, minimumPayment)
    : 0;

  total -= pointUsed;

  let transactionFee = 0;
  const usesTransactionFee = TRANSACTION_FEE_PRODUCT_TYPES.has(productType);

  if (
    PERCENTAGE_FEE_METHOD_IDS.has(paymentMethod?.id) &&
    usesTransactionFee &&
    total >= minimumPayment
  ) {
    transactionFee = Math.floor(total * 0.01);
    total += transactionFee;
  }

  if (
    paymentMethod?.type === "va" &&
    paymentMethod?.gateway !== "briapi" &&
    usesTransactionFee &&
    total >= minimumPayment
  ) {
    transactionFee = 4000;
    total += transactionFee;
  }

  let belowMinimumFee = 0;
  if (total > 0 && total < minimumPayment) {
    belowMinimumFee = minimumPayment - total;
    total = minimumPayment;
  }

  return {
    additionalFee: transactionFee + belowMinimumFee,
    belowMinimumFee,
    pointUsed,
    total,
    transactionFee,
    // Null untuk metode lain. Kodenya tidak ditambahkan ke `total` karena
    // besarnya belum diketahui siapa pun saat ini.
    uniqueCode:
      paymentMethod?.id === "bri_transfer" && total > 0 ? UNIQUE_CODE : null,
  };
};
